import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Navigation } from '../../components/Navigation';
import { Footer } from '../../components/Footer';
import { SideCart } from '../../components/SideCart';
import { ChatBot } from '../../components/ChatBot';
import { useCart } from '../../context/CartContext';
import { getAllPosts, getCategories } from '../../data/blogData';

const formatDate = (date, options = { day: 'numeric', month: 'long', year: 'numeric' }) =>
  new Date(date).toLocaleDateString('en-US', options);

function FeaturedArticle({ post }) {
  const navigate = useNavigate();

  return (
    <motion.article
      onClick={() => navigate(`/blog/${post.slug}`)}
      className="group cursor-pointer border-y"
      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="grid md:grid-cols-2 min-h-[500px]">
        <div className="flex flex-col justify-center px-5 py-10 sm:px-6 md:px-12 md:py-16" style={{ background: '#FAF9F6' }}>
          <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
            Featured Article
          </p>
          <h2 className="text-4xl md:text-5xl font-light leading-tight mb-5 transition-colors group-hover:text-[#B8704D]" style={{ color: '#1A2A1A' }}>
            {post.title}
          </h2>
          <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(26,42,26,0.58)', maxWidth: '48ch' }}>
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs mb-8" style={{ color: 'rgba(26,42,26,0.45)' }}>
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.date)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>

          <span className="inline-flex items-center gap-3 text-sm font-medium tracking-wide transition-all group-hover:gap-5" style={{ color: '#B8704D' }}>
            Read Article
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        <div className="relative min-h-[320px] overflow-hidden" style={{ background: '#F0EDE6' }}>
          <motion.img
            src={post.image}
            alt={post.title}
            className="absolute inset-0 h-full w-full object-cover"
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <div className="absolute left-5 top-5 sm:left-8 sm:top-8">
            <span className="px-3 py-2 text-[10px] font-medium tracking-[0.2em] uppercase" style={{ background: '#FAF9F6', color: '#B8704D' }}>
              {post.category}
            </span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

function ArticleCard({ post, index }) {
  const navigate = useNavigate();

  return (
    <motion.article
      onClick={() => navigate(`/blog/${post.slug}`)}
      className="group cursor-pointer border-b"
      style={{ borderColor: 'rgba(26,42,26,0.08)' }}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay: Math.min(index * 0.04, 0.24), ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="grid grid-cols-[88px_1fr] gap-5 py-6 sm:grid-cols-[140px_1fr] md:grid-cols-[220px_1fr_auto] md:gap-8 md:py-8">
        <div className="relative aspect-[4/5] overflow-hidden sm:aspect-[5/4] md:aspect-[4/3]" style={{ background: '#F0EDE6' }}>
          <img
            src={post.image}
            alt={post.title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>

        <div className="min-w-0 self-center">
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] tracking-[0.18em] uppercase" style={{ color: 'rgba(26,42,26,0.38)' }}>
            <span style={{ color: '#B8704D' }}>{post.category}</span>
            <span>{formatDate(post.date, { day: 'numeric', month: 'short' })}</span>
            <span>{post.readTime}</span>
          </div>

          <h3 className="text-xl md:text-2xl font-light leading-tight mb-3 transition-colors group-hover:text-[#B8704D]" style={{ color: '#1A2A1A' }}>
            {post.title}
          </h3>
          <p className="hidden text-sm leading-relaxed sm:line-clamp-2" style={{ color: 'rgba(26,42,26,0.55)' }}>
            {post.excerpt}
          </p>
        </div>

        <div className="hidden md:flex items-center pr-4" style={{ color: '#B8704D' }}>
          <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </motion.article>
  );
}

export function BlogPage() {
  const { isCartOpen, setIsCartOpen, cartItems, updateQuantity, removeItem, cartItemsCount } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true });

  const allPosts = getAllPosts();
  const categories = ['All', ...getCategories()];
  const featuredPost = allPosts[0];
  const filteredPosts = selectedCategory === 'All'
    ? allPosts.slice(1)
    : allPosts.filter((post) => post.category === selectedCategory);

  const categoryCounts = allPosts.reduce(
    (acc, post) => ({ ...acc, [post.category]: (acc[post.category] || 0) + 1 }),
    { All: allPosts.length }
  );

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#FAF9F6' }}>
      <Navigation
        onCartOpen={() => setIsCartOpen(true)}
        cartItemsCount={cartItemsCount}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pb-20" style={{ background: '#FAF9F6' }}>
        <div className="container mx-auto px-5 sm:px-6">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 24 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.3em] uppercase mb-6" style={{ color: '#B8704D' }}>
              Berleto Journal
            </p>
            <div className="grid md:grid-cols-[1fr_280px] gap-8 md:items-end">
              <h1 className="text-5xl md:text-7xl font-light leading-tight" style={{ color: '#1A2A1A' }}>
                Field notes for<br />
                <span style={{ color: 'rgba(26,42,26,0.28)' }}>modern wellness.</span>
              </h1>
              <p className="text-sm leading-relaxed md:pb-2" style={{ color: 'rgba(26,42,26,0.55)' }}>
                Evidence, rituals, and practical guidance on mushrooms, adaptogens, focus, recovery, and everyday performance.
              </p>
            </div>
          </motion.div>

          <div className="mt-12 pt-6 border-t grid grid-cols-3" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
            {[
              { n: allPosts.length, label: 'Articles' },
              { n: getCategories().length, label: 'Categories' },
              { n: featuredPost.readTime, label: 'Featured read' },
            ].map((item) => (
              <div key={item.label} className="px-2 py-3 sm:px-5 md:px-8 border-r last:border-r-0" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                <p className="text-2xl md:text-3xl font-light mb-1" style={{ color: '#1A2A1A' }}>{item.n}</p>
                <p className="text-[10px] md:text-xs tracking-[0.16em] md:tracking-widest uppercase leading-tight" style={{ color: 'rgba(26,42,26,0.4)' }}>{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <FeaturedArticle post={featuredPost} />

      {/* Filters and list */}
      <section className="pb-24" style={{ background: '#FAF9F6' }}>
        <div className="border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
          <div className="container mx-auto px-5 sm:px-6 py-8">
            <div className="flex gap-3 overflow-x-auto scrollbar-hide">
              {categories.map((category) => {
                const active = selectedCategory === category;

                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className="flex-none px-4 py-3 text-xs font-medium tracking-[0.18em] uppercase transition-colors border"
                    style={{
                      borderColor: active ? '#B8704D' : 'rgba(26,42,26,0.1)',
                      color: active ? '#FAF9F6' : 'rgba(26,42,26,0.55)',
                      background: active ? '#B8704D' : 'transparent',
                    }}
                  >
                    {category} <span style={{ opacity: 0.65 }}>{categoryCounts[category] || 0}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="container mx-auto px-5 sm:px-6">
          <div className="grid md:grid-cols-[220px_1fr] gap-10 md:gap-16 pt-10">
            <aside className="hidden md:block md:sticky md:top-32 self-start">
              <p className="text-xs font-medium tracking-[0.25em] uppercase mb-5" style={{ color: '#B8704D' }}>
                Latest Writing
              </p>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(26,42,26,0.52)' }}>
                Choose a topic, then move through the archive by image, signal, and read time.
              </p>
            </aside>

            <div>
              {filteredPosts.length > 0 ? (
                filteredPosts.map((post, index) => (
                  <ArticleCard key={post.id} post={post} index={index} />
                ))
              ) : (
                <div className="py-16 border-b" style={{ borderColor: 'rgba(26,42,26,0.08)' }}>
                  <p className="text-sm" style={{ color: 'rgba(26,42,26,0.55)' }}>
                    No articles found in this category.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <SideCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
      />

      <ChatBot />
    </div>
  );
}
